/** Memória do ambiente da IA (R01-16): avaliar e corrigir respostas, exemplos aprovados e o índice da memória. */
import { unwrap, type HiveClient, type components } from '@hive/api';

export type MemoryExample = components['schemas']['hive.core.ai.MemoryController.Example'];
export type MemoryStatus = components['schemas']['hive.core.ai.MemoryController.MemoryStatus'];
export type MemorySearch = components['schemas']['hive.core.ai.MemoryController.SearchResult'];
export type FeedbackInput = components['schemas']['hive.core.ai.MemoryController.FeedbackInput'];

export const EXAMPLE_STATES = ['pending', 'approved', 'rejected', 'rated'] as const;
export type ExampleState = (typeof EXAMPLE_STATES)[number];

export function stateTone(s?: string): 'warn' | 'ok' | 'crit' | 'info' {
  return s === 'pending' ? 'warn' : s === 'approved' ? 'ok' : s === 'rejected' ? 'crit' : 'info';
}

export const memoryApi = {
  feedback: (api: HiveClient, body: FeedbackInput) => unwrap(api.POST('/api/v1/answers/feedback', { body })),
  status: (api: HiveClient) => unwrap(api.GET('/api/v1/ai/memory')),
  reindex: (api: HiveClient) => unwrap(api.POST('/api/v1/ai/memory/reindex')),
  search: (api: HiveClient, question: string) => unwrap(api.POST('/api/v1/ai/memory/search', { body: { question } })),
  /** Todas as páginas (cursor) até o limite: a lista da tela filtra e ordena localmente. */
  async examples(api: HiveClient, max = 1000): Promise<MemoryExample[]> {
    const out: MemoryExample[] = [];
    let cursor: string | undefined;
    do {
      const page = await unwrap(api.GET('/api/v1/ai/memory/examples', { params: { query: { limit: 200, ...(cursor ? { cursor } : {}) } } }));
      out.push(...(page.items ?? []));
      cursor = page.next ?? undefined;
    } while (cursor && out.length < max);
    return out;
  },
  add: (api: HiveClient, question: string, answer: string) => unwrap(api.POST('/api/v1/ai/memory/examples', { body: { question, answer } })),
  approve: (api: HiveClient, id: string, correction?: string, note?: string) =>
    unwrap(api.POST('/api/v1/ai/memory/examples/{id}/approve', { params: { path: { id } }, body: { ...(correction ? { correction } : {}), ...(note ? { note } : {}) } })),
  reject: (api: HiveClient, id: string, note?: string) =>
    unwrap(api.POST('/api/v1/ai/memory/examples/{id}/reject', { params: { path: { id } }, body: { ...(note ? { note } : {}) } })),
  remove: (api: HiveClient, id: string) => unwrap(api.DELETE('/api/v1/ai/memory/examples/{id}', { params: { path: { id } } })),
};
